import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, AccountAssignments, Party, RateChangeResponse } from '../api';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { Btn, Card, EmptyState, RowSkeleton, SectionCard, StatePill, StatTile } from '../ui/components';
import {
  Button,
  ErrorBanner,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { usePinGate } from '../auth/pinGate';
import { EditAccountDialog } from '../accounts/EditAccountDialog';
import { NewAccountDialog } from '../accounts/NewAccountDialog';
import { AliasEditorDialog } from '../accounts/AliasEditorDialog';
import { ChangeHolderDialog } from '../accounts/ChangeHolderDialog';
import { RateHistory } from '../accounts/RateHistory';
import { Modal } from '../ui/Modal';
import { EntityManageMenu } from '../components/EntityManageMenu';
import { CheckCircle2, Plus, Tag, History as HistoryIcon, UserCog, Users, Wallet, X } from 'lucide-react';

type AccountStatusFilter = 'active' | 'inactive' | 'all';

export function AccountsManagePage() {
  const readOnly = useReadOnly();
  const [statusFilter, setStatusFilter] = useState<AccountStatusFilter>('active');

  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts', statusFilter],
    queryFn: () =>
      api.get<Account[]>(
        `/api/accounts${statusFilter === 'all' ? '' : `?status=${statusFilter}`}`,
      ),
  });
  const uplinesQ = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });
  const clientsQ = useQuery<Party[]>({
    queryKey: ['parties', 'client'],
    queryFn: () => api.get<Party[]>('/api/parties?role=client'),
  });
  const [newAccountOpen, setNewAccountOpen] = useState(false);

  const uplineMap = new Map((uplinesQ.data ?? []).map((p) => [p.id, p.displayName]));

  // Group by upline
  const byUpline = new Map<number, Account[]>();
  for (const a of accountsQ.data ?? []) {
    const list = byUpline.get(a.uplinePartyId) ?? [];
    list.push(a);
    byUpline.set(a.uplinePartyId, list);
  }

  // KPI counts derived from already-fetched data
  const total = accountsQ.data?.length ?? 0;
  const activeCount = (accountsQ.data ?? []).filter((a) => a.status === 'active').length;
  const uplinesCount = byUpline.size;
  const statusLabel =
    statusFilter === 'active' ? 'active' : statusFilter === 'inactive' ? 'archived' : 'all';

  return (
    <AppShell
      title="Accounts"
      toolbar={
        <PageHeader
          section="accounts"
          title="Accounts"
          subtitle="Accounts, rates, and assignments"
          actions={
            !readOnly ? (
              <Btn size="sm" onClick={() => setNewAccountOpen(true)}>
                <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                <span className="hidden sm:inline">New account</span>
                <span className="sm:hidden">New</span>
              </Btn>
            ) : undefined
          }
        />
      }
    >
      <div className="space-y-4">
        {/* KPI tiles */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatTile
            section="accounts"
            variant="hero"
            icon={Wallet}
            label="Accounts"
            value={String(total)}
            subtext={statusLabel}
          />
          <StatTile
            section="accounts"
            icon={CheckCircle2}
            label="Active"
            value={String(activeCount)}
            subtext="currently active"
          />
          <StatTile
            section="accounts"
            icon={Users}
            label="Uplines"
            value={String(uplinesCount)}
            subtext="with accounts"
          />
        </div>

        {/* Status filter */}
        <div
          role="tablist"
          aria-label="Filter accounts by status"
          className="inline-flex rounded-xl border border-border bg-surface-raised p-1"
        >
          {([
            { v: 'active', label: 'Active' },
            { v: 'inactive', label: 'Archived' },
            { v: 'all', label: 'All' },
          ] as const).map((o) => {
            const active = statusFilter === o.v;
            return (
              <button
                key={o.v}
                role="tab"
                aria-selected={active}
                onClick={() => setStatusFilter(o.v)}
                className={[
                  'min-h-[36px] rounded-lg px-3 py-1 text-xs font-medium transition-colors',
                  active ? 'bg-sec-accounts text-white' : 'text-ink-muted hover:text-ink',
                ].join(' ')}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {accountsQ.isLoading && (
          <SectionCard section="accounts" padded={false}>
            <div className="px-4 py-3">
              <RowSkeleton rows={4} />
            </div>
          </SectionCard>
        )}
        {Array.from(byUpline.entries()).map(([uplineId, accounts]) => (
          <SectionCard
            key={uplineId}
            section="accounts"
            title={uplineMap.get(uplineId) ?? `Upline #${uplineId}`}
          >
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {accounts.map((a) => (
                <AccountCard
                  key={a.id}
                  account={a}
                  readOnly={readOnly}
                  clients={clientsQ.data ?? []}
                />
              ))}
            </div>
          </SectionCard>
        ))}
        {!accountsQ.isLoading && (accountsQ.data?.length ?? 0) === 0 && (
          <EmptyState
            section="accounts"
            icon={Wallet}
            title="No accounts yet"
            description="Create your first account to start importing weekly sheets."
            action={
              !readOnly ? (
                <Btn size="sm" onClick={() => setNewAccountOpen(true)}>
                  New account
                </Btn>
              ) : undefined
            }
          />
        )}
      </div>

      {newAccountOpen && (
        <NewAccountDialog
          uplines={uplinesQ.data ?? []}
          clients={clientsQ.data ?? []}
          onClose={() => setNewAccountOpen(false)}
          onCreated={() => setNewAccountOpen(false)}
        />
      )}
    </AppShell>
  );
}

function AccountCard({
  account,
  readOnly,
  clients,
}: {
  account: Account;
  readOnly: boolean;
  clients: Party[];
}) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [editing, setEditing] = useState(false);
  const [editMetaOpen, setEditMetaOpen] = useState(false);
  const [aliasOpen, setAliasOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [holderOpen, setHolderOpen] = useState(false);

  const assignmentsQ = useQuery<AccountAssignments>({
    queryKey: ['accounts', account.id, 'assignments'],
    queryFn: () => api.get<AccountAssignments>(`/api/accounts/${account.id}/assignments`),
  });

  const active = assignmentsQ.data?.activeAssignment;

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-small font-medium text-ink">
            {account.canonicalName}
          </p>
          {account.panel && (
            <p className="mt-0.5 text-caption text-ink-muted">{account.panel}</p>
          )}
        </div>
        <StatePill state={account.status} />
      </div>

      {assignmentsQ.isLoading ? (
        <p className="mt-3 text-caption text-ink-muted">Loading rates…</p>
      ) : active ? (
        <dl className="mt-3 space-y-1.5 text-small">
          <div className="flex justify-between">
            <dt className="text-ink-muted">Upline rate</dt>
            <dd className="font-mono text-ink">{ratePct(active.uplineRate)}</dd>
          </div>
          {active.clientRate && (
            <div className="flex justify-between">
              <dt className="text-ink-muted">Client rate</dt>
              <dd className="font-mono text-ink">{ratePct(active.clientRate)}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-ink-muted">Active from</dt>
            <dd className="font-mono text-ink-muted">{active.effectiveFrom}</dd>
          </div>
        </dl>
      ) : (
        <p className="mt-3 text-caption font-medium text-warn">No active assignment</p>
      )}

      {account.notes && (
        <p className="mt-2 border-l-2 border-border pl-2 text-caption text-ink-muted">
          {account.notes}
        </p>
      )}

      {!readOnly && !editing && (
        <div className="mt-3 flex gap-2">
          {active && (
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 justify-center"
              onClick={() => setEditing(true)}
            >
              Edit rates
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-center"
            onClick={() => setEditMetaOpen(true)}
          >
            Edit details
          </Button>
        </div>
      )}

      {!readOnly && !editing && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAliasOpen(true)}>
            <Tag className="h-3.5 w-3.5" strokeWidth={2} /> Aliases
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
            <HistoryIcon className="h-3.5 w-3.5" strokeWidth={2} /> History
          </Button>
          {active && (
            <Button variant="ghost" size="sm" onClick={() => setHolderOpen(true)}>
              <UserCog className="h-3.5 w-3.5" strokeWidth={2} /> Change holder
            </Button>
          )}
        </div>
      )}

      {!readOnly && !editing && (
        <div className="mt-2 border-t border-border pt-2">
          <EntityManageMenu
            kind="accounts"
            id={account.id}
            noun="account"
            isArchived={account.status !== 'active'}
            archivePinGated={false}
            onChanged={() => void qc.invalidateQueries({ queryKey: ['accounts'] })}
          />
        </div>
      )}

      {!readOnly && editing && active && (
        <RateEditForm
          account={account}
          currentUplineRate={active.uplineRate}
          currentClientRate={active.clientRate ?? ''}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void qc.invalidateQueries({ queryKey: ['accounts', account.id, 'assignments'] });
          }}
          withPin={withPin}
        />
      )}

      {editMetaOpen && (
        <EditAccountDialog
          account={account}
          onClose={() => setEditMetaOpen(false)}
          onSaved={() => setEditMetaOpen(false)}
        />
      )}

      {aliasOpen && (
        <AliasEditorDialog account={account} onClose={() => setAliasOpen(false)} />
      )}

      {holderOpen && active && (
        <ChangeHolderDialog
          account={account}
          clients={clients}
          currentUplineRate={active.uplineRate}
          currentClientPartyId={active.clientPartyId}
          onClose={() => setHolderOpen(false)}
          onSaved={() => {
            setHolderOpen(false);
            void qc.invalidateQueries({ queryKey: ['accounts', account.id, 'assignments'] });
          }}
        />
      )}

      {historyOpen && (
        <Modal onClose={() => setHistoryOpen(false)} size="lg">
          <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
            <h2 className="text-small font-semibold text-ink">
              Rate history — {account.canonicalName}
            </h2>
            <button
              type="button"
              onClick={() => setHistoryOpen(false)}
              aria-label="Close"
              className="rounded-md p-1 text-ink-muted hover:bg-surface-raised hover:text-ink"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-4">
            <RateHistory accountId={account.id} />
          </div>
        </Modal>
      )}
    </Card>
  );
}

// Rates are stored as decimal multipliers (cost = points × rate; 1 = 100%).
// The UI shows / accepts them as plain percentages.
function ratePct(rate: string): string {
  const n = Number(rate);
  if (!Number.isFinite(n)) return rate;
  return `${+(n * 100).toFixed(4)}%`;
}
function rateToPctInput(rate: string): string {
  const n = Number(rate);
  if (!Number.isFinite(n)) return '';
  return String(+(n * 100).toFixed(4));
}
function pctInputToRate(pct: string): string {
  const n = Number(pct);
  if (!Number.isFinite(n)) return pct.trim();
  return String(+(n / 100).toFixed(8));
}

function RateEditForm({
  account,
  currentUplineRate,
  currentClientRate,
  onCancel,
  onSaved,
  withPin,
}: {
  account: Account;
  currentUplineRate: string;
  currentClientRate: string;
  onCancel: () => void;
  onSaved: () => void;
  withPin: ReturnType<typeof usePinGate>['withPin'];
}) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [uplineRate, setUplineRate] = useState(rateToPctInput(currentUplineRate));
  const [clientRate, setClientRate] = useState(
    currentClientRate ? rateToPctInput(currentClientRate) : '',
  );
  const [equalSpread, setEqualSpread] = useState(currentUplineRate === currentClientRate);
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const mut = useMutation({
    mutationFn: () => {
      const uplineMult = pctInputToRate(uplineRate);
      const clientMult = equalSpread
        ? uplineMult
        : clientRate.trim()
          ? pctInputToRate(clientRate)
          : null;
      return api.patch<RateChangeResponse>(`/api/accounts/${account.id}/rate`, {
        uplineRate: uplineMult,
        clientRate: clientMult,
        equalSpread,
        effectiveFrom,
      });
    },
    onSuccess: () => {
      setError(null);
      setOk(true);
      setTimeout(onSaved, 800);
    },
    onError: (e) => {
      setOk(false);
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <form
      className="mt-3 space-y-3 border-t border-border pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        setOk(false);
        void withPin(() => mut.mutateAsync());
      }}
    >
      <TextInput
        label="Upline rate (cost %)"
        value={uplineRate}
        onChange={(e) => {
          setUplineRate(e.target.value);
          if (equalSpread) setClientRate(e.target.value);
        }}
        name="uplineRate"
        inputMode="text"
        hint="Percent, e.g. 6 for 6% — negatives allowed (e.g. -6)"
      />

      <label className="flex cursor-pointer select-none items-center gap-2 text-small text-ink-muted">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-border bg-surface accent-sec-accounts"
          checked={equalSpread}
          onChange={(e) => {
            setEqualSpread(e.target.checked);
            if (e.target.checked) setClientRate(uplineRate);
          }}
        />
        Equal spread (client rate = upline rate)
      </label>

      {!equalSpread && (
        <TextInput
          label="Client rate (%)"
          value={clientRate}
          onChange={(e) => setClientRate(e.target.value)}
          name="clientRate"
          inputMode="text"
          hint="Percent, e.g. 6 (negatives allowed). Blank for self-held"
        />
      )}

      <TextInput
        label="Effective from"
        type="date"
        value={effectiveFrom}
        onChange={(e) => setEffectiveFrom(e.target.value)}
        name="effectiveFrom"
      />

      {error && <ErrorBanner>{error}</ErrorBanner>}
      {ok && <p className="text-caption text-pos">Rate updated.</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? <Spinner /> : 'Save rate change'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

